import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import FiltersSidebar from './FiltersSidebar';
import { settingsNavItems } from '../config/sidebar-config';
import type { SidebarCategory } from '../config/sidebar-config';

// Settings left-rail navigation. Reuses the FiltersSidebar shell so it shares
// the collapse animation, mobile slide-in/backdrop, and header chrome with the
// Orders/Customers filter panels — but the body is a navigation menu, not
// filters. The slot is the same; the content type differs (nav vs. filter),
// which is the intended divergence: Settings routes you between destinations,
// the list pages narrow a dataset.
const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  link: {
    display: 'block',
    boxSizing: 'border-box',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('7px', '10px'),
    fontFamily: 'inherit',
    fontSize: '13px',
    color: tokens.colorNeutralForeground1,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  linkActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },
  // Category header echoes the FilterSection label (uppercase, bold) so the
  // nav reads as the same family as the filter panels, but it navigates to the
  // section page on click and exposes a chevron to expand/collapse its anchors.
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('8px'),
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('7px', '10px'),
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground1,
  },
  sectionLabelActive: {
    color: tokens.colorBrandForeground2,
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    display: 'inline-flex',
  },
  subList: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
    ...shorthands.padding('2px', '0', '4px', '0'),
  },
  subLink: {
    ...shorthands.padding('6px', '10px', '6px', '22px'),
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
});

const CategorySection: React.FC<{ category: SidebarCategory }> = ({ category }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = !!category.path && location.pathname.startsWith(category.path);
  // Default the active section open; others start collapsed to keep the rail short.
  const [open, setOpen] = useState(isActive);
  return (
    <div>
      <button
        type="button"
        className={styles.sectionHeader}
        aria-expanded={open}
        onClick={() => {
          // Toggle on header click. Opening navigates to the section page;
          // collapsing just closes the subitems (no navigation away).
          if (open) {
            setOpen(false);
          } else {
            if (category.path) navigate(category.path);
            setOpen(true);
          }
        }}
      >
        <span className={`${styles.sectionLabel} ${isActive ? styles.sectionLabelActive : ''}`}>
          {category.label}
        </span>
        <span
          className={styles.chevron}
          role="button"
          aria-label={open ? 'Collapse section' : 'Expand section'}
          onClick={(e) => {
            // Toggle without navigating — the chevron only opens/closes.
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        </span>
      </button>
      {open && (
        <div className={styles.subList}>
          {category.items.map((sub) => {
            const active = location.pathname + (location.hash || '') === sub.path;
            return (
              <button
                key={sub.value}
                type="button"
                className={`${styles.link} ${styles.subLink} ${active ? styles.linkActive : ''}`}
                onClick={() => navigate(sub.path)}
              >
                {sub.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface SettingsNavSidebarProps {
  collapsed: boolean;
  /** Mobile-only: invoked when the slide-in backdrop is tapped. */
  onRequestClose?: () => void;
}

const SettingsNavSidebar: React.FC<SettingsNavSidebarProps> = ({ collapsed, onRequestClose }) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <FiltersSidebar
      collapsed={collapsed}
      activeFilterCount={0}
      hasActiveFilters={false}
      onClearAll={() => {}}
      hideClearAll
      title="Settings"
      onRequestClose={onRequestClose}
    >
      <div className={styles.list}>
        {settingsNavItems.map((item) => {
          if (item.type === 'category') {
            return <CategorySection key={item.value} category={item} />;
          }
          const active = location.pathname === item.path;
          return (
            <button
              key={item.value}
              type="button"
              className={`${styles.link} ${active ? styles.linkActive : ''}`}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    </FiltersSidebar>
  );
};

export default SettingsNavSidebar;
